import { useState } from "react";
import { ArrowLeft, Send, Loader2, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import LanguageSwitcher from "../components/LanguageSwitcher";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ASSETS = {
  logo: "/logo.png",
};

export default function WithdrawalForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    products: "",
    order_number: "",
    order_date: "",
    received_date: "",
    customer_name: "",
    customer_address: "",
    customer_email: "",
    customer_phone: "",
    reason: "",
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await fetch(`${API}/withdrawal`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      setSubmitted(true);
      toast.success("Withdrawal form submitted successfully");
    } catch (error) {
      toast.error("Failed to submit. Please try again or email contact@gafferathletics.com");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="border-b border-white/10 p-6 md:p-8">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-6">
            <a href="/" className="text-white/60 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </a>
            <img src={ASSETS.logo} alt="Gaffer Athletics" className="h-8" />
          </div>
          <LanguageSwitcher />
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 md:px-8 py-12 md:py-16">
        <h1 className="heading-xl text-3xl md:text-4xl text-white mb-4">Withdrawal Form</h1>
        <p className="text-white/60 mb-8">
          In accordance with EU consumer protection law, you have the right to withdraw from your purchase within 14 days without giving any reason.
        </p>

        {submitted ? (
          <div className="border border-white/20 p-12 text-center space-y-4">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto" />
            <h2 className="heading-md text-xl text-white">Withdrawal Request Submitted</h2>
            <p className="text-white/60 text-sm">
              We've received your withdrawal request and will process it within 5 business days. 
              A confirmation has been sent to <strong className="text-white">{formData.customer_email}</strong>.
            </p>
            <p className="text-white/40 text-sm mt-4">
              Please return the goods to the address below within 14 days.
            </p>
            <div className="border border-white/10 p-6 bg-white/[0.02] mt-6 text-left max-w-sm mx-auto">
              <p className="text-white/40 text-xs uppercase tracking-wider mb-2">Return Address</p>
              <p className="text-white text-sm">
                Gaffer Athletics ApS<br />
                Hørvænget 14, 8870 Langå<br />
                Denmark
              </p>
            </div>
            <a href="/" className="btn-secondary inline-block mt-6 px-6 py-3 text-sm">
              Back to Homepage
            </a>
          </div>
        ) : (
          <div className="space-y-8 body-text text-white/70">
            {/* Online Form */}
            <form onSubmit={handleSubmit} className="space-y-8">
              <section className="border border-white/10 p-8">
                <h2 className="heading-md text-xl text-white mb-6">Order Details</h2>
                <div className="space-y-4">
                  <div>
                    <label className="text-white/60 text-sm block mb-1">Product(s) *</label>
                    <input
                      data-testid="withdrawal-products"
                      type="text"
                      name="products"
                      value={formData.products}
                      onChange={handleChange}
                      required
                      placeholder="e.g. The Gaffer, EU 43"
                      className="input-minimal w-full"
                    />
                  </div>
                  <div>
                    <label className="text-white/60 text-sm block mb-1">Order Number *</label>
                    <input
                      data-testid="withdrawal-order-number"
                      type="text"
                      name="order_number"
                      value={formData.order_number}
                      onChange={handleChange}
                      required
                      placeholder="e.g. GA-20260324-A1B2C3"
                      className="input-minimal w-full"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-white/60 text-sm block mb-1">Ordered on</label>
                      <input
                        type="date"
                        name="order_date"
                        value={formData.order_date}
                        onChange={handleChange}
                        className="input-minimal w-full"
                      />
                    </div>
                    <div>
                      <label className="text-white/60 text-sm block mb-1">Received on</label>
                      <input
                        type="date"
                        name="received_date"
                        value={formData.received_date}
                        onChange={handleChange}
                        className="input-minimal w-full"
                      />
                    </div>
                  </div>
                </div>
              </section>

              <section className="border border-white/10 p-8">
                <h2 className="heading-md text-xl text-white mb-6">Your Information</h2>
                <div className="space-y-4">
                  <div>
                    <label className="text-white/60 text-sm block mb-1">Full Name *</label>
                    <input
                      data-testid="withdrawal-name"
                      type="text"
                      name="customer_name"
                      value={formData.customer_name}
                      onChange={handleChange}
                      required
                      className="input-minimal w-full"
                    />
                  </div>
                  <div>
                    <label className="text-white/60 text-sm block mb-1">Address *</label>
                    <textarea
                      data-testid="withdrawal-address"
                      name="customer_address"
                      value={formData.customer_address}
                      onChange={handleChange}
                      required
                      rows={3}
                      className="input-minimal w-full resize-none"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-white/60 text-sm block mb-1">Email *</label>
                      <input
                        data-testid="withdrawal-email"
                        type="email"
                        name="customer_email"
                        value={formData.customer_email}
                        onChange={handleChange}
                        required
                        className="input-minimal w-full"
                      />
                    </div>
                    <div>
                      <label className="text-white/60 text-sm block mb-1">Phone (optional)</label>
                      <input
                        type="tel"
                        name="customer_phone"
                        value={formData.customer_phone}
                        onChange={handleChange}
                        className="input-minimal w-full"
                      />
                    </div>
                  </div>
                </div>
              </section>

              <section className="border border-white/10 p-8">
                <h2 className="heading-md text-xl text-white mb-6">Reason for Withdrawal (optional)</h2>
                <textarea
                  data-testid="withdrawal-reason"
                  name="reason"
                  value={formData.reason}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Please let us know why you wish to withdraw (optional)"
                  className="input-minimal w-full resize-none"
                />
              </section>

              <button
                data-testid="withdrawal-submit"
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full py-4 flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Submit Withdrawal Request
                  </>
                )}
              </button>
            </form>

            {/* Important Information */}
            <section>
              <h2 className="heading-md text-xl text-white mb-4">Important Information</h2>
              <ul className="list-disc list-inside space-y-2 text-white/60 ml-4">
                <li>The withdrawal period expires 14 days from the day you received the goods</li>
                <li>You must return the goods within 14 days of informing us of your withdrawal</li>
                <li>The goods must be returned in their original condition and packaging</li>
                <li>You are responsible for the direct cost of returning the goods</li>
                <li>We will reimburse you within 14 days of receiving the returned goods</li>
                <li>Refunds will be made using the same payment method used for the original transaction</li>
              </ul>
            </section>

            {/* Return Address */}
            <section className="p-6 border border-white/10 bg-white/5">
              <h2 className="heading-md text-lg text-white mb-4">Return Address</h2>
              <p className="text-sm">
                Please return goods to:<br /><br />
                <strong className="text-white">Gaffer Athletics ApS</strong><br />
                Hørvænget 14, 8870 Langå<br />
                Denmark
              </p>
            </section>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 p-6 md:p-8">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-white/30 text-sm">
            © 2026 Gaffer Athletics. Engineering for the Sideline.
          </p>
        </div>
      </footer>
    </div>
  );
}
